import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const columns = [
  { label: 'Company Name', key: 'company_name' },
  { label: 'Address', key: 'address' },
  { label: 'PAN', key: 'pancard' },
  { label: 'City', key: 'city' },
  { label: 'State', key: 'state' },
  { label: 'Country', key: 'country' },
  { label: 'GST Number', key: 'gst_number' },
  { label: 'GST UIN', key: 'gst_uin' },
  { label: 'Code', key: 'code' },
  { label: 'CIN', key: 'cin' },
  { label: 'Email', key: 'email' },
];

interface CustomerType {
  id: number | string;
  company_name?: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  [key: string]: any;
}

const Customer: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const navigate = useNavigate();

  const fetchCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/auth/customers`);
      setCustomers(res.data);
    } catch (err) {
      setError('Error loading customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleAdd = () => {
    navigate('/addcustomer/?customer_id=new');
  };

  const handleEdit = (id: number | string) => {
    navigate(`/addcustomer/?customer_id=${id}`);
  };

  const handleDelete = async (id: number | string) => {
    setDeleteError('');
    const confirmed = window.confirm('Are you sure you want to delete this customer?');
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/auth/customer/${id}`);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setDeleteError('Error deleting customer');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Customers</h2>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          Add Customer
        </button>
      </div>
      {error && <div className="text-red-600 mb-2">{error}</div>}
      {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium"
                >
                  {col.label}
                </th>
              ))}
              <th className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6">Loading...</td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6">
                  No customers found.
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50 transition">
                  {columns.map((col) => (
                    <td key={col.key} className="py-2 px-4 border-b text-sm">
                      {customer[col.key] || <span className="text-gray-400">-</span>}
                    </td>
                  ))}
                  <td className="py-2 px-4 border-b">
                    <button
                      className="px-3 py-1 mr-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-xs"
                      onClick={() => handleEdit(customer.id)}
                      disabled={deletingId === customer.id}
                    >
                      Edit
                    </button>
                    <button
                      className={`px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition text-xs ${
                        deletingId === customer.id ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                      onClick={() => handleDelete(customer.id)}
                      disabled={deletingId === customer.id}
                    >
                      {deletingId === customer.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Customer;